import { useCallback, useEffect, useMemo, useState } from "react";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { useSession } from "../../hooks/useSession";
import { supabase } from "../../lib/supabaseClient";
import "./Admin.css";

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

const isValidHttpUrl = (value) => {
    if (!value) return true;
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

export default function Admin() {
    const { session, loading: sessionLoading } = useSession();
    const { isAdmin, loading: adminLoading } = useIsAdmin();

    // data
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    // per-card edit state
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState(null);
    const [rowBusyId, setRowBusyId] = useState(null);

    // ---------- Data ----------
    const loadPending = useCallback(async () => {
        setLoading(true);
        setMsg("");

        // NOTE: This requires an FK relationship term_submissions.submitted_by_user_id -> profiles.user_id
        // and a schema cache reload in Supabase.
        const { data, error } = await supabase
            .from("term_submissions")
            .select(
                `
                id,
                term,
                definition,
                category,
                tags,
                approved,
                created_at,
                palia_pedia_u,
                submitted_by_user_id,
                profiles!term_submissions_submitted_by_user_id_fkey (
                    username,
                    display_name
                )
            `,
            )
            .eq("approved", false)
            .order("created_at", { ascending: false });

        setLoading(false);

        if (error) {
            setMsg(error.message);
            return;
        }

        setPending(data || []);
    }, []);

    // load when admin status becomes true
    useEffect(() => {
        if (session?.user && isAdmin) {
            loadPending();
        } else {
            // if they sign out or lose admin, clear list + editing state
            setPending([]);
            setEditingId(null);
            setEditDraft(null);
        }
    }, [session?.user, isAdmin, loadPending]);

    const signOut = async () => {
        setMsg("");
        await supabase.auth.signOut();
        setPending([]);
        setEditingId(null);
        setEditDraft(null);
    };

    // ---------- Edit helpers ----------
    const startEdit = (item) => {
        setEditingId(item.id);
        setEditDraft({
            term: item.term || "",
            definition: item.definition || "",
            category: item.category || "",
            tags: item.tags || "",
            palia_pedia_u: item.palia_pedia_u || "",
        });
        setMsg("");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft(null);
    };

    const onEditChange = (e) => {
        const { name, value } = e.target;
        setEditDraft((prev) => ({ ...prev, [name]: value }));
    };

    const validateEdit = (d) => {
        const errs = [];
        if (!d.term.trim()) errs.push("Term is required.");
        if (!d.definition.trim()) errs.push("Definition is required.");
        if (!d.category.trim()) errs.push("Category is required.");
        if (
            d.palia_pedia_u?.trim() &&
            !isValidHttpUrl(d.palia_pedia_u.trim())
        ) {
            errs.push("Link must be a valid http(s) URL.");
        }
        return errs;
    };

    const saveEdit = async (id) => {
        if (!editDraft) return;

        const errs = validateEdit(editDraft);
        if (errs.length) {
            setMsg(errs.join(" "));
            return;
        }

        setRowBusyId(id);
        setMsg("");

        const { error } = await supabase
            .from("term_submissions")
            .update({
                term: editDraft.term.trim(),
                definition: editDraft.definition.trim(),
                category: editDraft.category.trim(),
                tags: editDraft.tags.trim(),
                palia_pedia_u: editDraft.palia_pedia_u.trim() || null,
            })
            .eq("id", id);

        setRowBusyId(null);

        if (error) {
            setMsg(error.message);
            return;
        }

        await loadPending();
        cancelEdit();
    };

    const approve = async (id) => {
        setRowBusyId(id);
        setMsg("");

        const { error } = await supabase
            .from("term_submissions")
            .update({ approved: true })
            .eq("id", id);

        setRowBusyId(null);

        if (error) {
            setMsg(error.message);
            return;
        }

        await loadPending();
    };

    const reject = async (id) => {
        setRowBusyId(id);
        setMsg("");

        const { error } = await supabase
            .from("term_submissions")
            .delete()
            .eq("id", id);

        setRowBusyId(null);

        if (error) {
            setMsg(error.message);
            return;
        }

        await loadPending();
    };

    const pendingCount = useMemo(() => pending.length, [pending]);

    // ---------- UI: loading session/admin ----------
    if (sessionLoading || adminLoading) {
        return (
            <div className="admin-container">
                <div className="admin-card">
                    <h2>Admin</h2>
                    <p className="admin-subtext">Checking permissions…</p>
                </div>
            </div>
        );
    }

    // ---------- UI: not logged in ----------
    if (!session?.user) {
        return (
            <div className="admin-container">
                <div className="admin-card">
                    <h2>Admin</h2>
                    <p className="admin-subtext">
                        You must be logged in to access the admin panel.
                    </p>

                    <div className="admin-actions">
                        <a className="admin-login-btn" href="/login">
                            Log in
                        </a>
                        <a className="admin-secondary" href="/signup">
                            Create account
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // ---------- UI: not admin ----------
    if (!isAdmin) {
        return (
            <div className="admin-container">
                <div className="admin-card">
                    <h2>Not authorized</h2>
                    <p className="admin-subtext">
                        This page is only available to admins.
                    </p>

                    <div className="admin-actions">
                        <a className="admin-login-btn" href="/">
                            Back home
                        </a>
                        <button className="admin-secondary" onClick={signOut}>
                            Sign out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ---------- UI: admin ----------
    return (
        <div className="admin-container">
            <div className="admin-card admin-header-card">
                <div className="admin-header">
                    <h2>Admin</h2>

                    <div className="admin-header-actions">
                        <button
                            className="admin-secondary"
                            onClick={loadPending}
                            disabled={loading}
                        >
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>
                        <button className="admin-secondary" onClick={signOut}>
                            Sign out
                        </button>
                    </div>
                </div>

                <p className="admin-subtext">
                    Pending submissions: <strong>{pendingCount}</strong>
                </p>

                {msg && <div className="admin-message">{msg}</div>}
            </div>

            {loading ? (
                <div className="admin-card">
                    <p className="admin-subtext">Loading…</p>
                </div>
            ) : pending.length === 0 ? (
                <div className="admin-card">
                    <p className="admin-subtext">No pending submissions 🎉</p>
                </div>
            ) : (
                <div className="admin-feed">
                    {pending.map((item) => {
                        const isEditing = editingId === item.id;
                        const busy = rowBusyId === item.id;

                        const submitter =
                            item.profiles?.display_name ||
                            item.profiles?.username ||
                            "Unknown";

                        return (
                            <div key={item.id} className="submission-card">
                                <div className="submission-top">
                                    <div className="submission-badge">
                                        PENDING
                                    </div>
                                    <div className="submission-meta">
                                        <div>
                                            Submitted by{" "}
                                            <strong>{submitter}</strong>
                                        </div>
                                        <div className="submission-date">
                                            {fmtDate(item.created_at)}
                                        </div>
                                    </div>
                                </div>

                                {!isEditing ? (
                                    <>
                                        <div className="submission-title">
                                            {item.term}
                                        </div>
                                        <div className="submission-body">
                                            {item.definition}
                                        </div>

                                        <div className="submission-tags">
                                            <span className="pill">
                                                {item.category ||
                                                    "Uncategorized"}
                                            </span>
                                            {item.tags
                                                ? item.tags
                                                      .split(",")
                                                      .map((t) => t.trim())
                                                      .filter(Boolean)
                                                      .slice(0, 8)
                                                      .map((t) => (
                                                          <span
                                                              key={t}
                                                              className="pill pill-muted"
                                                          >
                                                              {t}
                                                          </span>
                                                      ))
                                                : null}
                                        </div>

                                        {item.palia_pedia_u ? (
                                            <div className="submission-link">
                                                <a
                                                    href={item.palia_pedia_u}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View source
                                                </a>
                                            </div>
                                        ) : null}

                                        <div className="submission-actions">
                                            <button
                                                className="admin-secondary"
                                                onClick={() => startEdit(item)}
                                                disabled={busy}
                                            >
                                                Edit
                                            </button>

                                            <button
                                                className="admin-primary"
                                                onClick={() => approve(item.id)}
                                                disabled={busy}
                                            >
                                                {busy
                                                    ? "Working..."
                                                    : "Approve"}
                                            </button>

                                            <button
                                                className="admin-danger"
                                                onClick={() => reject(item.id)}
                                                disabled={busy}
                                            >
                                                Reject
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="admin-field">
                                            <label>Term</label>
                                            <input
                                                name="term"
                                                value={editDraft?.term ?? ""}
                                                onChange={onEditChange}
                                            />
                                        </div>

                                        <div className="admin-field">
                                            <label>Definition</label>
                                            <textarea
                                                name="definition"
                                                rows={4}
                                                value={
                                                    editDraft?.definition ?? ""
                                                }
                                                onChange={onEditChange}
                                            />
                                        </div>

                                        <div className="admin-field">
                                            <label>Category</label>
                                            <input
                                                name="category"
                                                value={
                                                    editDraft?.category ?? ""
                                                }
                                                onChange={onEditChange}
                                            />
                                        </div>

                                        <div className="admin-field">
                                            <label>
                                                Tags (comma-separated)
                                            </label>
                                            <input
                                                name="tags"
                                                value={editDraft?.tags ?? ""}
                                                onChange={onEditChange}
                                            />
                                        </div>

                                        <div className="admin-field">
                                            <label>
                                                PaliaPedia Link (optional)
                                            </label>
                                            <input
                                                name="palia_pedia_u"
                                                value={
                                                    editDraft?.palia_pedia_u ??
                                                    ""
                                                }
                                                onChange={onEditChange}
                                            />
                                        </div>

                                        <div className="submission-actions">
                                            <button
                                                className="admin-secondary"
                                                onClick={cancelEdit}
                                                disabled={busy}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="admin-primary"
                                                onClick={() =>
                                                    saveEdit(item.id)
                                                }
                                                disabled={busy}
                                            >
                                                {busy ? "Saving..." : "Save"}
                                            </button>
                                            <button
                                                className="admin-danger"
                                                onClick={() => reject(item.id)}
                                                disabled={busy}
                                            >
                                                Reject
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
